import { useEffect, useRef } from 'react';

/**
 * Ref that flips to true the first time the user actually touches the page.
 *
 * react-dates announces focus changes on its own while props settle after mount, which is
 * indistinguishable at the handler from a real "user clicked Start Date to re-pick the
 * range" gesture - and the handlers that react to that gesture clear the selected dates.
 * Without this gate, a date range prefilled from the user's saved rental defaults gets
 * wiped milliseconds after it lands, with no user involvement at all. A genuine
 * interaction with the calendar is by definition preceded by a pointer or key event, so
 * gating on one costs nothing for the real case.
 *
 * Returns a ref (not state) deliberately: reading it must never itself trigger a render,
 * and callers only ever read it from inside event handlers.
 *
 * @returns {React.MutableRefObject<boolean>} ref whose `.current` is true after the first
 * pointerdown/keydown anywhere on the page.
 */
export const useHasUserInteracted = () => {
  const hasUserInteractedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const markInteracted = () => {
      hasUserInteractedRef.current = true;
    };
    // Capture phase: the flag has to be set before the focus handler that reads it runs.
    window.addEventListener('pointerdown', markInteracted, { capture: true });
    window.addEventListener('keydown', markInteracted, { capture: true });
    return () => {
      window.removeEventListener('pointerdown', markInteracted, { capture: true });
      window.removeEventListener('keydown', markInteracted, { capture: true });
    };
  }, []);

  return hasUserInteractedRef;
};

export default useHasUserInteracted;
